<template>
  <div>
    <b-card
      no-body
      :class="isDark ? 'analytics-hearings analytics-hearings--dark' : 'analytics-hearings analytics-hearings--light'"
    >
      <template #header>
        <b-col>
          <b-row class="justify-content-between align-items-center">
            <b-col class="p-0 m-0">
              <p class="p-0 m-0 analytics-hearings__title">
                Listed Cases
              </p>
            </b-col>
            <b-col>
              <b-row class="justify-content-end align-items-center">
                <b-button
                  v-b-tooltip.hover
                  variant="outline-primary"
                  class="mr-1 p-1 d-inline-flex align-items-center justify-content-center analytics-hearings__calendar-trigger"
                  title="Open Calendar"
                  @click="onCalendarClick"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="24"
                    class="analytics-hearings__calendar-icon"
                  />
                </b-button>
                <hearing-filter class="pr-50" />
                <hearing-download-button />
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </template>

      <!-- Tabs for Confirmed and Tentative -->
      <div class="custom-tabs-row mt-1">
        <div
          :class="['custom-tab', activeTab === 0 ? 'active' : '', isDark ? 'custom-tab--dark' : 'custom-tab--light']"
          @click="activeTab = 0"
        >
          <span :class="['custom-tab-title', activeTab === 0 ? 'custom-tab-title--active' : '']">Confirmed</span>
        </div>
        <div
          :class="['custom-tab', activeTab === 1 ? 'active' : '', 'custom-tab--tentative', isDark ? 'custom-tab--dark' : 'custom-tab--light']"
          @click="activeTab = 1"
        >
          <span :class="['custom-tab-title', activeTab === 1 ? 'custom-tab-title--active-tentative' : '']">Tentative</span>
        </div>
      </div>

      <b-overlay
        :show="hearingLoading && !filteredHearings.length"
        rounded="sm"
        variant="transparent"
        opacity="0.5"
        blur="2px"
      >
        <div class="analytics-hearings__body">
          <div v-if="filteredHearings.length">
            <b-card
              v-for="item in filteredHearings"
              :key="item.date"
              no-body
              class="m-0 p-0 rounded-0"
            >
              <b-list-group flush>
                <b-list-group-item class="flex-column align-items-start">
                  <div class="d-flex w-100 justify-content-between">
                    <h4 class="mb-1 hearing-date--confirmed">
                      Hearing Date: {{ item.date }}
                    </h4>
                    <small class="text-secondary">{{ item.data.length }} Cases</small>
                  </div>
                  <analytics-hearing-item-list
                    :items="item.data"
                    :hearing_date="item.date"
                    :is-tentative="activeTab === 1"
                  />
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </div>

          <p
            v-if="!filteredHearings.length && !hearingLoading"
            class="text-center mt-5"
          >
            Record Not Found!
          </p>
        </div>
      </b-overlay>

      <b-card-footer class="m-0 p-0">
        <b-col>
          <b-row class="justify-content-center p-1">
            <b-button
              variant="outline-info"
              :disabled="!filteredHearings.length"
              @click="$router.push({ name: 'cause-list' })"
            >
              View More
            </b-button>
          </b-row>
        </b-col>
      </b-card-footer>
    </b-card>

    <!-- Calendar Modal -->
    <b-modal
      id="calendar-modal"
      v-model="isCalendarModalOpen"
      centered
      hide-footer
      dialog-class="analytics-hearings-modal__dialog"
      :modal-class="[
        'analytics-hearings-modal',
        isDark ? 'analytics-hearings-modal--dark' : 'analytics-hearings-modal--light'
      ]"
      :content-class="[
        'analytics-hearings-modal__content',
        isDark ? 'analytics-hearings-modal__content--dark' : 'analytics-hearings-modal__content--light'
      ]"
      body-class="analytics-hearings-modal__body"
    >
      <template #modal-header>
        <div class="analytics-hearings-modal__header">
          <h5 class="mb-0 analytics-hearings-modal__title">
            Hearings Calendar
          </h5>
          <b-button
            variant="link"
            class="analytics-hearings-modal__close"
            @click="isCalendarModalOpen = false"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </b-button>
        </div>
      </template>

      <div class="analytics-hearings-modal__calendar-shell">
        <div class="analytics-hearings-modal__calendar-topbar">
          <div class="analytics-hearings-modal__toolbar-left">
            <b-button
              variant="link"
              class="analytics-hearings-modal__nav-btn"
              @click="changeCalendarMonth(-1)"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </b-button>
            <b-button
              variant="link"
              class="analytics-hearings-modal__nav-btn"
              @click="changeCalendarMonth(1)"
            >
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </b-button>
            <h6 class="mb-0 analytics-hearings-modal__month-title">
              {{ calendarTitle }}
            </h6>
          </div>
        </div>

        <div class="analytics-hearings-modal__weekdays">
          <div
            v-for="weekday in calendarWeekdays"
            :key="weekday"
            class="analytics-hearings-modal__weekday"
          >
            {{ weekday }}
          </div>
        </div>

        <div class="analytics-hearings-modal__grid">
          <button
            v-for="day in calendarDays"
            :key="day.iso"
            type="button"
            :class="[
              'analytics-hearings-modal__day',
              {
                'analytics-hearings-modal__day--muted': !day.inMonth,
                'analytics-hearings-modal__day--today': day.isToday,
                'analytics-hearings-modal__day--selected': day.isSelected
              }
            ]"
            @click="selectCalendarDate(day.iso)"
          >
            <span class="analytics-hearings-modal__day-number">{{ day.label }}</span>
            <span
              v-if="day.count"
              :class="[
                'analytics-hearings-modal__day-badge',
                getCalendarBadgeClass(day.count)
              ]"
            >
              {{ day.count }}
            </span>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'
import moment from 'moment'
import {
  BButton,
  BCard,
  BCardFooter,
  BCol,
  BListGroup,
  BListGroupItem,
  BModal,
  BOverlay,
  BRow,
  VBTooltip,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import HearingDownloadButton from '../causelist/HearingDownloadButton.vue'
import HearingFilter from '../causelist/HearingFilter.vue'
import AnalyticsHearingItemList from './components/AnalyticsHearingItemList.vue'

export default {
  components: {
    BButton,
    BCard,
    BCardFooter,
    BCol,
    BListGroup,
    BListGroupItem,
    BModal,
    BOverlay,
    BRow,
    HearingDownloadButton,
    HearingFilter,
    AnalyticsHearingItemList,
    BTabs,
    BTab,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      interval: null,
      isCalendarModalOpen: false,
      selectedCalendarDate: moment().format('YYYY-MM-DD'),
      calendarCursor: moment().startOf('month').format('YYYY-MM-DD'),
      activeTab: 0, // 0: Confirmed, 1: Tentative
    }
  },
  computed: {
    hearings() {
      return this.$store.state.hearingStore.allHearings || []
    },
    hearingLoading() {
      return this.$store.state.hearingStore.hearingLoading
    },
    filteredHearings() {
      // Tab 0: Confirmed, Tab 1: Tentative
      if (this.activeTab === 1) {
        // Tentative: filter for both matterStatus and matter_status
        return this.hearings
          .map(item => ({
            ...item,
            data: (item.data || []).filter(h => h.matter_status === 'TENTATIVE'),
          }))
          .filter(item => item.data.length > 0)
      }
      // Confirmed: all except tentative
      return this.hearings
        .map(item => ({
          ...item,
          data: (item.data || []).filter(h => h.matter_status !== 'TENTATIVE'),
        }))
        .filter(item => item.data.length > 0)
    },
    calendarTitle() {
      return moment(this.calendarCursor).format('MMMM YYYY')
    },
    calendarWeekdays() {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    },
    hearingCountByDate() {
      return this.hearings.reduce((accumulator, item) => {
        const normalizedDate = this.normalizeHearingDate(item.date)
        if (normalizedDate) {
          const confirmedCount = (item.data || []).filter(h => h.matter_status !== 'TENTATIVE').length
          accumulator[normalizedDate] = (accumulator[normalizedDate] || 0) + confirmedCount
        }
        return accumulator
      }, {})
    },
    calendarDays() {
      const cursor = moment(this.calendarCursor).startOf('month')
      const gridStart = cursor.clone().startOf('week')
      const today = moment().format('YYYY-MM-DD')

      return Array.from({ length: 42 }, (_, index) => {
        const day = gridStart.clone().add(index, 'days')
        const iso = day.format('YYYY-MM-DD')
        return {
          iso,
          label: day.date(),
          inMonth: day.month() === cursor.month(),
          isToday: iso === today,
          isSelected: iso === this.selectedCalendarDate,
          count: this.hearingCountByDate[iso] || 0,
        }
      })
    },
  },
  beforeCreate() {
    this.$store.dispatch('hearingStore/fetchHearings')
  },
  beforeMount() {
    this.startInterval()
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  methods: {
    onCalendarClick() {
      this.isCalendarModalOpen = true
    },
    changeCalendarMonth(direction) {
      this.calendarCursor = moment(this.calendarCursor)
        .add(direction, 'month')
        .startOf('month')
        .format('YYYY-MM-DD')
    },
    selectCalendarDate(date) {
      this.selectedCalendarDate = date
      this.isCalendarModalOpen = false
      this.$router.push({
        name: 'cause-list',
        query: {
          scrollDate: moment(date).format('DD-MM-YYYY'),
        },
      })
    },
    normalizeHearingDate(date) {
      if (!date) return null
      const parsed = moment(date, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true)
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
    },
    getCalendarBadgeClass(count) {
      if (count > 20) return 'analytics-hearings-modal__day-badge--red'
      if (count >= 16) return 'analytics-hearings-modal__day-badge--orange'
      if (count >= 10) return 'analytics-hearings-modal__day-badge--yellow'
      if (count >= 5) return 'analytics-hearings-modal__day-badge--light-green'
      return 'analytics-hearings-modal__day-badge--dark-green'
    },
    startInterval() {
      const beforeTime = 17
      const afterTime = 9
      const now = Number(moment().format('HH'))
      const day = moment().format('dddd')

      if (day !== 'Sunday' && now > afterTime && now < beforeTime) {
        this.$store.dispatch('displayboardStore/fetchUserDisplayBoard')
        clearInterval(this.interval)
        this.interval = setInterval(() => {
          this.$store.dispatch('displayboardStore/fetchUserDisplayBoard')
        }, 30000)
      }
    },
  },
  setup() {
    const { skin } = useAppConfig()
    const isDark = computed(() => skin.value === 'dark')

    return { isDark, skin }
  },
}
</script>

<style scoped>
.analytics-hearings--dark {
  background-color: #343d55;
  border-color: #343d55;
  --hearing-card-bg: #283046;
  --hearing-card-text: #d0d2d6;
  --hearing-title: #7ab8f5;
  --hearing-tab-active: #7ab8f5;
  --hearing-tab-bg: #3b4253;
  --hearing-tab-tentative: #ff9800;
  --hearing-date-confirmed: #d0d2d6;
  --hearing-date-tentative: #ff9800;
}

.analytics-hearings--light {
  background-color: #f3f2f7;
  border-color: #e6e9eb;
  --hearing-card-bg: #ffffff;
  --hearing-card-text: #5e5873;
  --hearing-title: #7367f0;
  --hearing-tab-active: #7367f0;
  --hearing-tab-bg: #ebe9f1;
  --hearing-tab-tentative: #ff9800;
  --hearing-date-confirmed: #5e5873;
  --hearing-date-tentative: #ff9800;
}

.analytics-hearings__title {
  font-weight: 600;
}

.analytics-hearings__calendar-trigger {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  line-height: 1;
}

.analytics-hearings__calendar-trigger :deep(svg.feather) {
  width: 24px !important;
  height: 24px !important;
  stroke-width: 2px;
  display: block;
}

.analytics-hearings__body {
  max-height: 530px;
  min-height: 529px;
  overflow-y: scroll;
}

.analytics-hearings-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
}

.analytics-hearings-modal__title {
  font-weight: 700;
  color: var(--calendar-modal-text);
}

.analytics-hearings-modal__calendar-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.9rem 0.65rem;
  border-bottom: 1px solid var(--calendar-grid-border);
}

.analytics-hearings-modal__toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.analytics-hearings-modal__month-title {
  margin-left: 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--calendar-modal-text);
}

.analytics-hearings-modal__nav-btn {
  padding: 0;
  width: 28px;
  height: 28px;
  border: 0;
  color: var(--calendar-modal-muted-text);
}

.analytics-hearings-modal__close {
  padding: 0;
  color: var(--calendar-modal-muted-text);
}

.analytics-hearings-modal__calendar-shell {
  background: var(--calendar-shell-bg);
  border: 1px solid var(--calendar-shell-border);
  border-radius: 10px;
  overflow: hidden;
}

.analytics-hearings-modal__weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-top: 1px solid var(--calendar-grid-border);
  border-bottom: 1px solid var(--calendar-grid-border);
}

.analytics-hearings-modal__weekday {
  padding: 0.55rem 0.35rem;
  text-align: center;
  color: var(--calendar-weekday-text);
  font-weight: 600;
  font-size: 0.88rem;
}

.analytics-hearings-modal__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.analytics-hearings-modal__day {
  position: relative;
  min-height: 94px;
  border: 0;
  border-right: 1px solid var(--calendar-grid-border);
  border-bottom: 1px solid var(--calendar-grid-border);
  background: var(--calendar-day-bg);
  color: var(--calendar-day-text);
  text-align: left;
  padding: 0.65rem;
}

.analytics-hearings-modal__day:nth-child(7n) {
  border-right: 0;
}

.analytics-hearings-modal__day--muted {
  color: var(--calendar-day-muted-text);
}

.analytics-hearings-modal__day--today {
  background: var(--calendar-day-today-bg);
}

.analytics-hearings-modal__day--selected {
  box-shadow: inset 0 0 0 1px var(--calendar-day-selected-border);
}

.analytics-hearings-modal__day-number {
  font-size: 0.75rem;
  line-height: 1;
}

.analytics-hearings-modal__day-badge {
  position: absolute;
  top: 0.7rem;
  right: 0.7rem;
  min-width: 24px;
  height: 24px;
  padding: 0 0.4rem;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
}

.analytics-hearings-modal__day-badge--red {
  background: #e74c3c;
}

.analytics-hearings-modal__day-badge--orange {
  background: #f39c12;
}

.analytics-hearings-modal__day-badge--yellow {
  background: #f1c40f;
  color: #ffffff;
}

.analytics-hearings-modal__day-badge--light-green {
  background: #7ed957;
  color: #fff;
}

.analytics-hearings-modal__day-badge--dark-green {
  background: #1f7a3d;
  color: #fff;
}

.analytics-hearings-modal__content {
  background: var(--calendar-modal-bg);
  color: var(--calendar-modal-text);
  border-radius: 12px;
  width: 100%;
}

.analytics-hearings-modal__footer-note {
  margin-top: 0.75rem;
  color: var(--calendar-modal-muted-text);
  font-size: 0.85rem;
}

@media (max-width: 991.98px) {
  .analytics-hearings-modal__header {
    align-items: center;
  }

  .analytics-hearings-modal__day {
    min-height: 92px;
    padding: 0.6rem;
  }

}
.custom-tabs-row {
  display: flex;
  width: 100%;
  margin-bottom: 1.5rem;
}
.custom-tab {
  flex: 1 1 0;
  text-align: center;
  padding: 18px 0 10px 0;
  font-size: 1rem;
  font-weight: 500;
  color: #d1d5db;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}
.custom-tab.active {
  color: var(--hearing-tab-active);
  background: var(--hearing-tab-bg);
}
.custom-tab.active::after {
  content: '';
  display: block;
  height: 4px;
  background: var(--hearing-tab-active);
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 2px 2px 0 0;
}

.custom-tab--tentative.active::after {
  background: var(--hearing-tab-active);
}
.custom-tab-title {
  font-weight: 500;
  font-size: 1rem;
  transition: color 0.2s;
  color: var(--hearing-card-text);
}
.custom-tab-title--active {
  color: var(--hearing-tab-active) !important;
}
.custom-tab-title--active-tentative {
  color: var(--hearing-tab-active) !important;
}

.hearing-date--confirmed {
  color: var(--hearing-card-text);
}
.hearing-date--tentative {
  color: var(--hearing-date-tentative, #ff9800);
}
</style>

<style>
.analytics-hearings-modal--dark {
  --calendar-modal-bg: #252c44;
  --calendar-modal-text: #eef2ff;
  --calendar-modal-muted-text: rgba(255, 255, 255, 0.72);
  --calendar-shell-bg: #2d3550;
  --calendar-shell-border: rgba(255, 255, 255, 0.06);
  --calendar-grid-border: rgba(255, 255, 255, 0.08);
  --calendar-weekday-text: #c9cfde;
  --calendar-day-bg: #313954;
  --calendar-day-text: #e4e8f5;
  --calendar-day-muted-text: rgba(199, 205, 223, 0.28);
  --calendar-day-today-bg: #212944;
  --calendar-day-selected-border: rgba(76, 163, 255, 0.7);
}

.analytics-hearings-modal--light {
  --calendar-modal-bg: #ffffff;
  --calendar-modal-text: #1f2937;
  --calendar-modal-muted-text: rgba(31, 41, 55, 0.65);
  --calendar-shell-bg: #f8fafc;
  --calendar-shell-border: rgba(148, 163, 184, 0.28);
  --calendar-grid-border: rgba(148, 163, 184, 0.25);
  --calendar-weekday-text: #475569;
  --calendar-day-bg: #ffffff;
  --calendar-day-text: #334155;
  --calendar-day-muted-text: rgba(100, 116, 139, 0.45);
  --calendar-day-today-bg: #e8f1ff;
  --calendar-day-selected-border: rgba(59, 130, 246, 0.65);
}

.analytics-hearings-modal__dialog {
  width: 40vw !important;
  max-width: 40vw !important;
  margin: 0 !important;
}

.analytics-hearings-modal__dialog .modal-content {
  width: 100%;
}
.analytics-hearings-modal {
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
}

@media (max-width: 991.98px) {
  .analytics-hearings-modal__dialog {
    width: calc(100vw - 2rem) !important;
    max-width: calc(100vw - 2rem) !important;
  }
}
</style>
